import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../../../../context/auth/auth-context'
import axios from 'axios'
import clsx from 'clsx'
import {
  type AlertColor, Box, Button, Chip, FormControl, FormControlLabel, IconButton, InputLabel, List, ListItem,
  ListItemText, MenuItem, Modal, Select, Stack, Switch, Typography, useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import SettingsIcon from '@mui/icons-material/Settings'
import SaveIcon from '@mui/icons-material/Save'
import { RISK_SOURCE, type InitialRisk, type StrategyInitialRisk } from '../../../../models/loader/initial-risk'
import { formAlertErrorMessage, formAlertSuccessMessage, isEmptyString, toTitleCase } from '../../../../utils/string-utils'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../../../utils/time-utils'
import { formatRawUsdAmount, formatRoundedUsdAmount } from '../../../../utils/rounding-formatting-utils'
import ViewJSONTrigger from '../view/view-json-trigger'
import ToastWithMessage from '../../../../components/toast/toast-with-message'

interface RiskLoaderProps {
  open: boolean
  onClose: () => void
  strategyId: string
  title: string
  dataUrl: string | null
  postUrl: string | null
}

const EMPTY_RISK_SOURCE = {
  [RISK_SOURCE.CONFIG]: [],
  [RISK_SOURCE.PAIRED_NODE]: [],
  [RISK_SOURCE.STRATEGY_DATABASE]: [],
}

const RiskLoaderModal: React.FC<RiskLoaderProps> = (
  { open, onClose, strategyId, title, dataUrl, postUrl }
) => {
  const theme = useTheme()
  const [symbolOrder, setSymbolOrder] = useState<string[]>([])
  const [isLocked, setIsLocked] = useState<boolean>(true)
  const [currentSelectedSource, setCurrentSelectedSource] = useState<RISK_SOURCE | ''>('')
  const [sourceData, setSourceData] = useState<Record<RISK_SOURCE, InitialRisk[]>>(EMPTY_RISK_SOURCE)
  const [retrievalTimestamp, setRetrievalTimestamp] = useState<number>(0)

  const [hoveredSymbol, setHoveredSymbol] = useState<string | null>(null)
  const [isDisplayHigherPrecision, setIsDisplayHigherPrecision] = useState<boolean>(false)
  const [userSelectedSource, setUserSelectedSource] = useState<RISK_SOURCE | ''>('')

  const { isAuthorized, accessToken } = useContext(AuthContext)

  const getRiskSourcesData = useCallback(() => {
    if (isEmptyString(dataUrl) || dataUrl == null) {
      return
    }

    axios
      .get(dataUrl, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        const data: StrategyInitialRisk = response.data[strategyId]

        const configRisk = data.sources[RISK_SOURCE.CONFIG]
        const symbolOrder = configRisk
          .filter(item => item.venueProduct !== 'USDCUSDT')
          .sort((a, b) => {
            const aSplits = a.venueProduct.split('_')
            const bSplits = b.venueProduct.split('_')
            if (aSplits.length > bSplits.length) {
              return 1
            } else if (aSplits.length < bSplits.length) {
              return -1
            }

            for (let i = 0; i < aSplits.length; i++) {
              const localeCompareResult = aSplits[i].localeCompare(bSplits[i], undefined, { numeric: true })
              if (localeCompareResult !== 0) {
                return localeCompareResult
              }
            }
            return 0
          })
          .map(item => item.venueProduct)

        setSymbolOrder(symbolOrder)
        setIsLocked(data.locked)
        setCurrentSelectedSource(data.selected)
        setSourceData(data.sources)
        setRetrievalTimestamp(Date.now())
      })
      .catch(reason => {
        console.error(reason)
        setSymbolOrder([])
        setIsLocked(true)
        setCurrentSelectedSource('')
        setSourceData(EMPTY_RISK_SOURCE)
        setRetrievalTimestamp(-1)
      })
  }, [isAuthorized, accessToken, strategyId, dataUrl])

  useEffect(() => {
    if (open && isAuthorized) {
      getRiskSourcesData()
    }
  }, [open, isAuthorized])

  const loadRiskSource = useCallback(() => {
    if (postUrl == null || postUrl.length === 0) {
      console.debug('[Risk Loader Modal] Failed to load risk source (reason: invalid url)')
      displayToastWithMessage('The POST url is invalid - please check the related configuration again', 'warning')
      return
    }

    if (isEmptyString(userSelectedSource)) {
      console.debug('[Risk Loader Modal] No risk source selected - skipping export')
      displayToastWithMessage('No risk source selected - skipping export', 'warning')
      return
    }

    const message = {
      timestamp: new Date().toISOString(),
      strategyId,
      command: `initialRisk,set,${userSelectedSource}`,
    }

    axios
      .post(postUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        console.debug(response)
        displayToastWithMessage(formAlertSuccessMessage(response.status, response.statusText, response.data.message), 'success')
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
      .finally(() => {
        getRiskSourcesData()
      })
  }, [isAuthorized, accessToken, strategyId, postUrl, userSelectedSource])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [strategyId])

  const symbolsStack: React.ReactNode = useMemo<React.ReactNode>(() => {
    const children = symbolOrder.map(symbol => {
      return (
        <ListItem
          key={`symbol-${symbol}`}
          disablePadding
          sx={{
            '& .hover.active': {
              color: 'primary.main',
            },
          }}
        >
          <Box
            width={1}
            className={clsx('hover', {
              active: hoveredSymbol === symbol,
            })}
            onMouseOver={() => {
              setHoveredSymbol(symbol)
            }}
            onMouseLeave={() => {
              setHoveredSymbol(null)
            }}
          >
            <ListItemText primary={symbol}/>
          </Box>
        </ListItem>
      )
    })

    return (
      <List sx={{ minWidth: 'fit-content' }} disablePadding>
        <ListItemText primary='Symbols' primaryTypographyProps={{ fontSize: 'large', fontWeight: 'bold' }}/>
        {children}
      </List>
    )
  }, [sourceData, symbolOrder, hoveredSymbol])

  const sourcesStack: React.ReactNode[] = useMemo<React.ReactNode[]>(() => {
    return Object.keys(sourceData)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
      .map(source => {
        const data = sourceData[source as RISK_SOURCE]

        const allSymbols = data.filter(item => item.venueProduct !== 'USDCUSDT').map(item => item.venueProduct)
        const deltaSymbols = allSymbols.filter(item => !symbolOrder.includes(item))

        const children =
          symbolOrder
            .map(symbol => {
              const matchingInitialRisk = data.find(item => item.venueProduct === symbol)
              let notional = '-'

              if (matchingInitialRisk != null) {
                notional = isDisplayHigherPrecision
                  ? formatRawUsdAmount(String(matchingInitialRisk.notional))
                  : formatRoundedUsdAmount(String(matchingInitialRisk.notional))
              }
              return (
                <ListItem
                  key={symbol}
                  disablePadding
                  sx={{
                    '& .hover.active': {
                      color: 'primary.main'
                    }
                  }}
                >
                  <Box
                    width={1}
                    textAlign='right'
                    className={clsx('hover', {
                      active: hoveredSymbol === symbol
                    })}
                    onMouseOver={() => {
                      setHoveredSymbol(symbol)
                    }}
                    onMouseLeave={() => {
                      setHoveredSymbol(null)
                    }}
                  >
                    <ListItemText primary={notional}/>
                  </Box>
                </ListItem>
              )
            })

        const deltaChildren =
          deltaSymbols
            .map(symbol => {
              const matchingInitialRisk = data.find(item => item.venueProduct === symbol)
              let notional = 'No Data'

              if (matchingInitialRisk != null) {
                notional = isDisplayHigherPrecision
                  ? formatRawUsdAmount(String(matchingInitialRisk.notional))
                  : formatRoundedUsdAmount(String(matchingInitialRisk.notional))
              }
              return (
                <ListItem
                  key={symbol}
                  disablePadding
                  sx={{
                    '& .hover.active': {
                      color: 'primary.main'
                    }
                  }}
                >
                  <Box
                    width={1}
                    textAlign='right'
                    className={clsx('hover', {
                      active: hoveredSymbol === symbol
                    })}
                    onMouseOver={() => {
                      setHoveredSymbol(symbol)
                    }}
                    onMouseLeave={() => {
                      setHoveredSymbol(null)
                    }}
                  >
                    <ListItemText primary={symbol}/>
                    <ListItemText primary={notional}/>
                  </Box>
                </ListItem>
              )
            })

        return (
          <List key={source} sx={{ minWidth: 'fit-content' }} disablePadding>
            <ListItemText
              primary={toTitleCase(source.replaceAll('_', ' '))}
              primaryTypographyProps={{ fontSize: 'large', fontWeight: 'bold', textAlign: 'right' }}
            />
            {children}
            {deltaChildren}
          </List>
        )
      })
  }, [sourceData, symbolOrder, hoveredSymbol, isDisplayHigherPrecision])

  const onModalClose = useCallback(() => {
    setToastOpen(false)
    onClose()
  }, [onClose])

  return (
    <Modal
      open={open}
      onClose={onModalClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Stack
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1600px',
          height: '80%',
          maxHeight: '1000px',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: 24,
          p: 4,
          color: 'white',
          overflowY: 'auto',
        }}
        gap={2}
      >
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography id='modal-modal-title' fontWeight='bold' variant='h5' component='h1'>
            {title}
          </Typography>
          <Chip
            label={formatToLocalMilliTimestampFromUTCNumber(retrievalTimestamp)}
            size='small'
          />
          <ViewJSONTrigger dataUrl={dataUrl}/>
          <Box marginX='auto'></Box>
          <IconButton color='error' onClick={onModalClose}>
            <CloseIcon/>
          </IconButton>
        </Stack>

        <Button variant='outlined' sx={{ width: 'fit-content' }} startIcon={<RefreshIcon/>} onClick={getRiskSourcesData}>
          Reload
        </Button>

        <Typography color='primary' fontWeight='bold' variant='h6' component='h2'>Risk Loader Status</Typography>

        <Stack gap={1} direction='row'>
          <Chip
            icon={isLocked ? <LockIcon/> : <LockOpenIcon/>}
            color={isLocked ? 'error' : 'success'}
            label={isLocked ? 'Locked' : 'Unlocked'}
          />
          <Chip
            icon={<SettingsIcon/>}
            color={isLocked ? 'default' : 'success'}
            label={`Source: ${toTitleCase(currentSelectedSource?.replaceAll('_', ' ') ?? 'Unknown')}`}
          />
        </Stack>

        <Typography color='primary' fontWeight='bold' variant='h6' component='h2' marginTop={4}>Initial Risk Sources</Typography>

        <FormControlLabel
          label='Display Numbers With Higher Precision'
          control={
            <Switch
              checked={isDisplayHigherPrecision}
              onChange={(_event, checked) => { setIsDisplayHigherPrecision(checked) }}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          }
        />

        <Stack direction='row' gap={4} paddingBottom={1} sx={{ overflowX: 'auto' }}>
          {symbolsStack}
          {sourcesStack}
        </Stack>

        <Stack gap={2}>
          <FormControl variant='standard' sx={{ width: 'min(400px,100%)' }}>
            <InputLabel id='source-select-label'>Initial Risk Source</InputLabel>
            <Select
              labelId='source-select-label'
              id='source-select'
              autoWidth
              label='Source'
              value={userSelectedSource}
              onChange={event => {
                setUserSelectedSource(event.target.value as RISK_SOURCE)
              }}
            >
              {
                Object.keys(RISK_SOURCE)
                  .map(source => {
                    return (
                      <MenuItem key={source} value={source}>{toTitleCase(source.replaceAll('_', ' '))}</MenuItem>
                    )
                  })
              }
            </Select>
          </FormControl>
          <Button variant='outlined' sx={{ width: 'min(400px,100%)' }} startIcon={<SaveIcon/>} onClick={loadRiskSource}>
            Load Risk Source
          </Button>
        </Stack>

        <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
      </Stack>
    </Modal>
  )
}

export default RiskLoaderModal
